import type { IntlShape } from 'react-intl';

import { ErrorKeySet, type ValidationError, type Validator } from '../../../types/field-validation';
import type { FormFieldValidation } from '../../../types/form/form-fields';
import type { LocalTime } from '../../../types/form/form-fields/field-values';
import { getFailedRequiredValidationError, getFailedValidationError } from '../utils';
import { messages } from './messages';

const datePrefix = '1999-01-01';

export const getTimeValidationErrors = (
	validation: FormFieldValidation,
	fieldLabel: string,
	intl?: IntlShape,
	localTime?: LocalTime,
): ReturnType<Validator> => {
	if (Object.keys(validation).length === 0) {
		return [];
	}

	if (!localTime) {
		if (validation.required) {
			return [getFailedRequiredValidationError(fieldLabel, intl)];
		}
		return [];
	}

	const time = new Date(`${datePrefix}T${localTime}`);
	const validationErrors: ValidationError[] = [];

	if (Number.isNaN(time.getTime())) {
		// Check time is valid
		validationErrors.push(
			getFailedValidationError(ErrorKeySet.INVALID, intl?.formatMessage(messages.invalidTime)),
		);
	} else if (validation.minimumTime && validation.minimumTime === validation.maximumTime) {
		// Exact time scenario
		const minTime = new Date(`${datePrefix}T${validation.minimumTime}`);
		if (time.getTime() !== minTime.getTime()) {
			validationErrors.push(
				getFailedValidationError(
					ErrorKeySet.INVALID,
					intl?.formatMessage(messages.exactTime, { time: minTime }),
				),
			);
		}
	} else if (validation.minimumTime && validation.maximumTime) {
		// Between times scenario
		const minTime = new Date(`${datePrefix}T${validation.minimumTime}`);
		const maxTime = new Date(`${datePrefix}T${validation.maximumTime}`);
		if (time < minTime || time > maxTime) {
			validationErrors.push(
				getFailedValidationError(
					ErrorKeySet.INVALID,
					intl?.formatMessage(messages.betweenTime, { minimum: minTime, maximum: maxTime }),
				),
			);
		}
	} else {
		if (validation.minimumTime) {
			const minTime = new Date(`${datePrefix}T${validation.minimumTime}`);
			if (time < minTime) {
				validationErrors.push(
					getFailedValidationError(
						ErrorKeySet.INVALID,
						intl?.formatMessage(messages.minimumTime, { minimum: minTime }),
					),
				);
			}
		}
		if (validation.maximumTime) {
			const maxTime = new Date(`${datePrefix}T${validation.maximumTime}`);
			if (time > maxTime) {
				validationErrors.push(
					getFailedValidationError(
						ErrorKeySet.INVALID,
						intl?.formatMessage(messages.maximumTime, { maximum: maxTime }),
					),
				);
			}
		}
	}

	return validationErrors;
};
